import React, { useState } from "react";
import { cn } from "@/lib/utils";
import { Separator } from "@/components/ui/separator";
import {
    Tooltip,
    TooltipTrigger,
    TooltipContent,
} from "@/components/ui/tooltip";
import {
    X,
    Trash2,
    Tag,
    Star,
    Archive,
    ArchiveRestore,
    Bookmark,
    Share2,
    Search,
    FolderOpen,
    Link2,
    Hash,
    Copy,
    Check,
} from "lucide-react";

import MoveFolderModal from "../moveFolderDialog";
import { ActionButton, getStarProps } from "./CommonComponents";
import { useItemDetailsContext } from "./ItemDetailsContext";

// Feature flag to control delete icon visibility
const SHOW_DELETE_ICON = false;

const ItemDetailsHeader = () => {
    const {
        itemType,
        itemData,
        itemDetails,
        starLevel,
        isBookmarked,
        isArchive,
        handleClose,
        handleDeleteFolder,
        handleTagDialogOpen,
        handleStarClick,
        handleBookmarkClick,
        handleArchiveClick,
        handleShareLink,
        handleSearchWithin,
    } = useItemDetailsContext();

    const [copiedId, setCopiedId] = useState(false);

    const handleCopyId = async () => {
        await navigator.clipboard.writeText(itemDetails?.id);
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 2000);
    };

    const starProps = getStarProps(starLevel);
    const Icon = itemType === "folder" ? FolderOpen : Link2;

    return (
        <div className="relative flex items-center justify-between px-4 py-3 border-b bg-card shrink-0 w-full">
            {/* Left section - Title */}
            <div className="flex items-center gap-3">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-primary/10">
                    <Icon className="h-4 w-4 text-primary" />
                </div>
                <span className="text-base font-semibold tracking-tight">
                    {itemType === "folder" ? "Folder" : "Link"} Details
                </span>
            </div>

            {/* Center section - Project ID with copy */}
            {itemDetails?.id && (
                <div className="absolute left-1/2 -translate-x-1/2">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={handleCopyId}
                                className={cn(
                                    "flex items-center gap-2 px-3 py-1.5 rounded-md",
                                    "bg-muted/40 border border-border/50",
                                    "hover:bg-muted/60 transition-colors cursor-pointer",
                                    "focus:outline-none focus:ring-2 focus:ring-primary/20"
                                )}
                            >
                                <Hash className="h-4 w-4 text-muted-foreground" />
                                <span className="text-base font-bold">
                                    {itemDetails.id}
                                </span>
                                {copiedId ? (
                                    <Check className="h-4 w-4 text-emerald-500" />
                                ) : (
                                    <Copy className="h-4 w-4 text-muted-foreground" />
                                )}
                            </button>
                        </TooltipTrigger>
                        <TooltipContent>
                            {copiedId ? "Copied!" : "Click to copy ID"}
                        </TooltipContent>
                    </Tooltip>
                </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center gap-0.5">
                {SHOW_DELETE_ICON && (
                    <ActionButton tooltip="Delete" onClick={handleDeleteFolder}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                    </ActionButton>
                )}

                <MoveFolderModal folder_id={itemData.id} />

                <ActionButton tooltip="Tags" onClick={handleTagDialogOpen}>
                    <Tag className="h-4 w-4" />
                </ActionButton>

                <ActionButton
                    tooltip={starProps.tooltip}
                    onClick={handleStarClick}
                    active={starProps.filled}
                    activeColor={starProps.color}
                >
                    <Star
                        className={cn(
                            "h-4 w-4 transition-transform",
                            starProps.filled && "fill-current scale-110"
                        )}
                    />
                </ActionButton>

                <ActionButton
                    tooltip={isBookmarked ? "Remove Bookmark" : "Bookmark"}
                    onClick={handleBookmarkClick}
                    active={isBookmarked}
                    activeColor="text-primary"
                >
                    <Bookmark
                        className={cn(
                            "h-4 w-4 transition-transform",
                            isBookmarked && "fill-current scale-110"
                        )}
                    />
                </ActionButton>

                <ActionButton
                    tooltip={isArchive ? "Unarchive" : "Archive"}
                    onClick={handleArchiveClick}
                >
                    {isArchive ? (
                        <ArchiveRestore className="h-4 w-4" />
                    ) : (
                        <Archive className="h-4 w-4" />
                    )}
                </ActionButton>

                <ActionButton tooltip="Share Link" onClick={handleShareLink}>
                    <Share2 className="h-4 w-4" />
                </ActionButton>

                {itemType === "folder" && (
                    <ActionButton
                        tooltip="Search Within This Folder"
                        onClick={handleSearchWithin}
                    >
                        <Search className="h-4 w-4" />
                    </ActionButton>
                )}

                <Separator orientation="vertical" className="mx-1 h-6" />

                <ActionButton tooltip="Close" onClick={handleClose}>
                    <X className="h-4 w-4" />
                </ActionButton>
            </div>
        </div>
    );
};

export default ItemDetailsHeader;
